/**
 * Flow Renderer - Routes step types to appropriate renderers
 */

import React from 'react';
import { FlowStep, FlowContext } from '../core/types';
import { GuidedFlowController } from '../core/GuidedFlowController';
import { ChatStepRenderer } from './ChatStepRenderer';
import { ViewStepRenderer } from './ViewStepRenderer';
import { InteractionStepRenderer } from './InteractionStepRenderer';
import { TransitionRenderer } from './TransitionRenderer';
import { DecisionRenderer } from './DecisionRenderer';

export interface FlowRendererProps {
  step: FlowStep;
  context: FlowContext;
  controller: GuidedFlowController;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onCancel: () => void;
  autoProgressEnabled?: boolean;
}

export const FlowRenderer: React.FC<FlowRendererProps> = ({
  step,
  context,
  controller,
  onNext,
  autoProgressEnabled = false,
}) => {
  switch (step.type) {
    case 'chat':
    case 'celebration':
      return (
        <ChatStepRenderer
          config={step.chat!}
          context={context}
          controller={controller}
          onComplete={onNext}
          isCelebration={step.type === 'celebration'}
          autoAdvance={step.autoAdvance}
          autoAdvanceDelay={step.autoAdvanceDelay}
          autoProgressEnabled={autoProgressEnabled}
        />
      );

    case 'view':
      return (
        <ViewStepRenderer
          config={step.view!}
          context={context}
          controller={controller}
          onNext={onNext}
          autoAdvance={step.autoAdvance}
          autoAdvanceDelay={step.autoAdvanceDelay}
        />
      );

    case 'interaction':
      return (
        <InteractionStepRenderer
          config={step.interaction!}
          viewConfig={step.view}
          context={context}
          controller={controller}
          onComplete={onNext}
          autoProgressEnabled={autoProgressEnabled}
        />
      );

    case 'transition':
      return (
        <TransitionRenderer
          config={step.transition!}
          context={context}
          onComplete={onNext}
          autoAdvance={step.autoAdvance}
          autoAdvanceDelay={step.autoAdvanceDelay}
          autoProgressEnabled={autoProgressEnabled}
        />
      );

    case 'decision':
      return (
        <DecisionRenderer
          config={step.decision!}
          context={context}
          controller={controller}
          onDecision={(nextStepId) => {
            controller.goToStep(nextStepId);
          }}
          autoProgressEnabled={autoProgressEnabled}
        />
      );

    default:
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-white/60">Unknown step type: {step.type}</p>
        </div>
      );
  }
};
